import {
  IPV4_OCTET_MAX,
  IPV4_OCTETS,
  IPV4_PREFIX_MAX,
  IPV6_GROUP_MAX_DIGITS,
  IPV6_GROUPS,
  IPV6_PREFIX_MAX,
  MAC_HEX_DIGITS,
} from './constants.ts';

// Addresses as technicians and exports write them. A MAC arrives as
// "AA:BB:CC:DD:EE:FF", "aa-bb-cc-dd-ee-ff", or "aabb.ccdd.eeff"; the import
// stores one form and the Worker compares by digits alone, so the same
// device typed two ways is still one device. A subnet on a property's VLAN
// table is CIDR text, checked here so the API and the form refuse the same
// things.

/** The hex digits of a MAC, lower-cased, separators dropped; '' when there are none. */
export function macHexDigits(raw: string): string {
  return raw.toLowerCase().replaceAll(/[^0-9a-f]/gu, '');
}

/** A MAC is six octets: exactly twelve hex digits once the separators go. */
export function isMacAddress(raw: string): boolean {
  return macHexDigits(raw).length === MAC_HEX_DIGITS && /^[\s0-9a-f:.-]+$/iu.test(raw);
}

/** "AA-BB-CC-DD-EE-FF" → "aa:bb:cc:dd:ee:ff"; null when the text is not a MAC. */
export function formatMac(raw: string): string | null {
  if (!isMacAddress(raw)) {
    return null;
  }
  const digits = macHexDigits(raw);
  return (digits.match(/../gu) ?? []).join(':');
}

function isIpv4(text: string): boolean {
  const octets = text.split('.');
  return (
    octets.length === IPV4_OCTETS &&
    octets.every((octet) => /^\d{1,3}$/u.test(octet) && Number(octet) <= IPV4_OCTET_MAX)
  );
}

function isIpv6Group(group: string): boolean {
  return group.length > 0 && group.length <= IPV6_GROUP_MAX_DIGITS && /^[0-9a-f]+$/iu.test(group);
}

/** RFC 4291 text form: up to eight hex groups, one "::" at most, an IPv4 tail allowed. */
function isIpv6(text: string): boolean {
  const halves = text.split('::');
  if (halves.length > 2) {
    return false;
  }
  const groups = halves.flatMap((half) => (half === '' ? [] : half.split(':')));
  const last = groups.at(-1);
  let count = groups.length;
  if (last?.includes('.') === true) {
    if (!isIpv4(last)) {
      return false;
    }
    groups.pop();
    count += 1;
  }
  if (!groups.every((group) => isIpv6Group(group))) {
    return false;
  }
  return halves.length === 2 ? count < IPV6_GROUPS : count === IPV6_GROUPS;
}

/** An IPv4 or IPv6 address as written, without a prefix length or a zone. */
export function isIpAddress(raw: string): boolean {
  const text = raw.trim();
  return text.includes(':') ? isIpv6(text) : isIpv4(text);
}

/** A prefix length: digits, no leading zero, within the family's ceiling. */
function isPrefixLength(text: string, max: number): boolean {
  return /^(?:0|[1-9]\d{0,2})$/u.test(text) && Number(text) <= max;
}

/**
 * A subnet in CIDR form: an address, one slash, and a prefix length its
 * family allows ("10.0.10.0/24", "2001:db8:1::/64"). The host bits are not
 * checked — a technician writes the gateway's own address with the prefix
 * as readily as the network address, and both name the same subnet.
 */
export function isCidr(raw: string): boolean {
  const text = raw.trim();
  const parts = text.split('/');
  const [address, prefix] = parts;
  if (parts.length !== 2 || address === undefined || prefix === undefined) {
    return false;
  }
  return (
    isIpAddress(address) &&
    isPrefixLength(prefix, address.includes(':') ? IPV6_PREFIX_MAX : IPV4_PREFIX_MAX)
  );
}

// Both families, counted rather than only spelled (ROADMAP G3, G4). An
// address list, a free-space figure, and an out-of-subnet check all need the
// same three things: which family an address is written in, what number it
// stands for, and which subnet holds it. IPv6 does not fit in a double, so
// every address is a bigint here and every subnet carries its first and last
// address as bigints too. Nothing below allocates per host: a /64 is counted,
// never listed.

/** Which family an address or a subnet is written in. */
export type IpFamily = 'ipv4' | 'ipv6';

/** Bits in an address of each family; the prefix ceilings are the same numbers. */
const FAMILY_BITS: Readonly<Record<IpFamily, number>> = {
  ipv4: IPV4_PREFIX_MAX,
  ipv6: IPV6_PREFIX_MAX,
};

/** How many addresses a subnet of each family sets aside as network and broadcast. */
const RESERVED_ADDRESSES = 2n;
/** An IPv4 subnet keeps a network and a broadcast address only while it is wider than this. */
const IPV4_POINT_TO_POINT_PREFIX = 31;
const IPV6_GROUP_BITS = 16n;
const IPV4_OCTET_BITS = 8n;
const HEX = 16;
/** Bigint arithmetic reads better with its own zero and one than with casts. */
const ZERO = 0n;
const ONE = 1n;
/** The low bits of one octet and of one IPv6 group, for reading an address apart. */
const OCTET_MASK = 0xffn;
const GROUP_MASK = 0xff_ffn;

/** 'ipv4', 'ipv6', or null when the text is not an address at all. */
export function ipFamily(raw: string): IpFamily | null {
  const text = raw.trim();
  if (!isIpAddress(text)) {
    return null;
  }
  return text.includes(':') ? 'ipv6' : 'ipv4';
}

/** The family a CIDR subnet is written in, or null when the text is not one. */
export function cidrFamily(raw: string): IpFamily | null {
  return isCidr(raw) ? ipFamily(raw.trim().split('/', 1)[0] ?? '') : null;
}

function ipv4ToNumber(text: string): bigint {
  return text
    .split('.')
    .reduce((total, octet) => (total << IPV4_OCTET_BITS) + BigInt(Number(octet)), ZERO);
}

/** One half of an IPv6 address as hex groups, an IPv4 tail folded into two of them. */
function ipv6Half(half: string): string[] {
  const groups = half === '' ? [] : half.split(':');
  const last = groups.at(-1);
  if (last?.includes('.') === true) {
    groups.pop();
    const value = ipv4ToNumber(last);
    groups.push((value >> IPV6_GROUP_BITS).toString(HEX), (value & GROUP_MASK).toString(HEX));
  }
  return groups;
}

/** The eight groups of an IPv6 address, "::" expanded and an IPv4 tail folded in. */
function ipv6Groups(text: string): string[] {
  const halves = text.split('::');
  const head = ipv6Half(halves[0] ?? '');
  if (halves.length === 1) {
    return head;
  }
  const tail = ipv6Half(halves[1] ?? '');
  return [
    ...head,
    ...Array.from({ length: IPV6_GROUPS - head.length - tail.length }, () => '0'),
    ...tail,
  ];
}

/**
 * The number an address stands for, as a bigint, or null when the text is
 * not an address. The same scale for both families, so one comparison and
 * one subtraction serve the address list of either.
 */
export function ipToNumber(raw: string): bigint | null {
  const family = ipFamily(raw);
  const text = raw.trim();
  if (family === null) {
    return null;
  }
  if (family === 'ipv4') {
    return ipv4ToNumber(text);
  }
  return ipv6Groups(text).reduce(
    (total, group) => (total << IPV6_GROUP_BITS) + BigInt(Number.parseInt(group, HEX)),
    ZERO,
  );
}

/** An address back as text, in its family's own notation. */
export function numberToIp(value: bigint, family: IpFamily): string {
  if (family === 'ipv4') {
    return Array.from({ length: IPV4_OCTETS }, (_, index) =>
      String((value >> (BigInt(IPV4_OCTETS - 1 - index) * IPV4_OCTET_BITS)) & OCTET_MASK),
    ).join('.');
  }
  return Array.from({ length: IPV6_GROUPS }, (_, index) =>
    ((value >> (BigInt(IPV6_GROUPS - 1 - index) * IPV6_GROUP_BITS)) & GROUP_MASK).toString(HEX),
  ).join(':');
}

/** A subnet as numbers: the family, the prefix, and the range it covers. */
export interface Subnet {
  readonly family: IpFamily;
  readonly prefix: number;
  /** The network address (the host bits of whatever was typed are cleared). */
  readonly network: bigint;
  /** The last address in the block, network and broadcast included. */
  readonly last: bigint;
  /** How many addresses the block holds, broadcast and network included. */
  readonly size: bigint;
}

/**
 * A CIDR subnet as numbers, or null when the text is not a subnet. The host
 * bits of the address are cleared, so "10.0.10.1/24" and "10.0.10.0/24"
 * parse to the same block — a technician writes either.
 */
export function parseSubnet(raw: string): Subnet | null {
  const family = cidrFamily(raw);
  const [address, prefixText] = raw.trim().split('/');
  if (family === null || address === undefined || prefixText === undefined) {
    return null;
  }
  const value = ipToNumber(address);
  if (value === null) {
    return null;
  }
  const prefix = Number(prefixText);
  const hostBits = BigInt(FAMILY_BITS[family] - prefix);
  const size = ONE << hostBits;
  const network = (value >> hostBits) << hostBits;
  return { family, prefix, network, last: network + size - ONE, size };
}

/** Whether an address sits inside a subnet; false when either is unreadable or they differ in family. */
export function isInSubnet(address: string, subnet: Subnet): boolean {
  if (ipFamily(address) !== subnet.family) {
    return false;
  }
  const value = ipToNumber(address);
  return value !== null && value >= subnet.network && value <= subnet.last;
}

/**
 * How many addresses of a subnet a device can hold: every address in IPv6
 * and in an IPv4 point-to-point block, and all but the network and broadcast
 * addresses in a wider IPv4 one.
 */
export function usableAddresses(subnet: Subnet): bigint {
  if (subnet.family === 'ipv6' || subnet.prefix >= IPV4_POINT_TO_POINT_PREFIX) {
    return subnet.size;
  }
  return subnet.size - RESERVED_ADDRESSES;
}

/** Sort order for addresses: by family, then by value; unreadable text last, by text. */
export function compareAddresses(a: string, b: string): number {
  const left = ipToNumber(a);
  const right = ipToNumber(b);
  if (left === null || right === null) {
    return left === right ? a.localeCompare(b) : left === null ? 1 : -1;
  }
  const familyOrder = (ipFamily(a) === 'ipv6' ? 1 : 0) - (ipFamily(b) === 'ipv6' ? 1 : 0);
  if (familyOrder !== 0) {
    return familyOrder;
  }
  return left === right ? 0 : left < right ? -1 : 1;
}

/**
 * Whether an address falls inside a DHCP range written as two addresses
 * ("10.0.10.100" and "10.0.10.200"). A half-written range (one end blank, or
 * either end unreadable) holds nothing rather than everything.
 */
export function isInRange(address: string, start: string, end: string): boolean {
  const value = ipToNumber(address);
  const first = ipToNumber(start);
  const last = ipToNumber(end);
  if (value === null || first === null || last === null) {
    return false;
  }
  if (ipFamily(address) !== ipFamily(start) || ipFamily(start) !== ipFamily(end)) {
    return false;
  }
  return value >= first && value <= last;
}

/** How many addresses a DHCP range covers; 0 when it is not a readable range. */
export function rangeSize(start: string, end: string): bigint {
  const first = ipToNumber(start);
  const last = ipToNumber(end);
  if (first === null || last === null || ipFamily(start) !== ipFamily(end) || last < first) {
    return ZERO;
  }
  return last - first + ONE;
}
